import { RewardUnlockStrategy } from '@brandclub/types';

import { styled, useTheme } from '@mui/material';
import { RewardPillWithText } from '../../BrandclubLanding/components';
import { OutOfStockPill } from '../../BrandclubLanding/components/OutOfStockPill/OutOfStockPill';
import { brandclubStyles } from '../../styles';
import { ButtonName } from '../../theme/componentNames';
import { CampaignType } from '../../types/Campaign';
import { ProductRewardSearchResult } from '../../types/product';
import { getEntityImage } from '../../utils/assetUtils';
import { TABLET_SCREEN_SIZE } from '../../utils/constants';
import { getBuyOrViewProductMetaTag } from '../../utils/dataLayer';
import {
  getAddToBagText,
  getPurchaseRewardAmountForProduct,
  getSortedBuyBoxProducts,
  isProductEligibleForCheckout,
  isProductOutOfStock,
} from '../../utils/productUtils';
import { formatMoney, isShopifyProduct } from '../../utils/stringUtils';
import { Button } from '../Button';

interface PriceDialogContentProps {
  productsToList: ProductRewardSearchResult[];
  onBuyClick: (product: ProductRewardSearchResult) => void;
  onOpenRetailerProductPageClick: (product: ProductRewardSearchResult) => void;
  checkoutAllowedOfferType?: 'firstPartyOnly' | 'thirdPartyOnly' | 'both';
  disabledCheckout?: boolean;
  className?: string;
}

const ContentContainer = styled('div', {
  label: 'PriceDialogContentContainer',
})(({ theme }) => ({
  width: '100%',
  color: theme.vars.palette.text.primary,
  paddingBottom: 20,
  '.bc_header': {
    display: 'flex',
    alertItems: 'center',
    justifyContent: 'center',
    padding: '12px 0 20px 0',
    position: 'relative',
    '.bc_title': {
      fontSize: 24,
      fontWeight: 600,
      color: 'inherit',
    },
    '.bc_close': {
      position: 'absolute',
      right: 20,
      top: 20,
    },
  },
  '.product_price': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    paddingBottom: 20,
    maxHeight: 585,
    overflow: 'auto',
    ...brandclubStyles.customScrollbar,
    '.price_card': {
      color: 'inherit',
      cursor: 'default',
      border: `1px solid #ebeef2`,
      padding: '18px 20px',
      margin: '0 20px',
      width: '100%',
      maxWidth: 483,
      boxSizing: 'border-box',
      borderRadius: 6,
      display: 'flex',
      '.retailer_icon': {
        width: '50px',
        height: '50px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: '20px',
        marginTop: '5px',
        img: {
          maxWidth: '100%',
          maxHeight: '100%',
        },
        '.external_link': {
          '&:not([href])': {
            all: 'unset',
          },
        },
      },
      '.retailer_price': {
        flex: 1,
        div: {
          width: 'fit-content',
        },
        '.name': {
          fontSize: 14,
          fontWeight: 600,
        },
        '.price': {
          fontSize: 24,
          fontWeight: 600,
          color: `inherit !important`,
          [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
            fontSize: 22,
          },
        },
      },
    },
  },
  '.actions': {
    display: 'flex',
    gap: 20,
    justifyContent: 'flex-end',
    [`@media (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
      flexDirection: 'column',
      gap: 10,
      alignItems: 'center',
    },
  },
}));

export const PriceDialogContent = ({
  productsToList,
  onBuyClick,

  onOpenRetailerProductPageClick,
  checkoutAllowedOfferType,
  disabledCheckout,
  className,
}: PriceDialogContentProps) => {
  const theme = useTheme();
  const dialogTitle = (theme as any).content?.buyBoxDialog?.title || 'Ways to buy';
  const viewButtonText = (theme as any).content?.buyBoxDialog?.viewButtonText;

  const getBuyBoxButtonText = (item: any) => {
    const defaultText = getAddToBagText(item, checkoutAllowedOfferType, disabledCheckout);
    if (viewButtonText && defaultText === 'View') {
      return viewButtonText;
    }
    return defaultText;
  };

  const sortedProductListed = getSortedBuyBoxProducts(productsToList);

  return (
    <ContentContainer className={className} id="price-dialog-content">
      <div className="bc_header">
        <div className="bc_title">{dialogTitle}</div>
      </div>
      <div className="product_price">
        {sortedProductListed.map((item: any, idx: number) => {
          let hasActiveInstantReward = false;
          for (const campaignReward of item?.campaignRewards ?? []) {
            if (
              campaignReward?.campaignType === CampaignType.DailyDealsRewardCampaign ||
              campaignReward?.rewardUnlockStrategy === RewardUnlockStrategy.Instant
            ) {
              hasActiveInstantReward = true;
              break;
            }
          }

          const rewardAmount = getPurchaseRewardAmountForProduct(item);
          const rewardPercent = !!item.retailPrice && !!rewardAmount ? (rewardAmount * 100) / item.retailPrice : 0;

          const isOutOfStock = isProductOutOfStock(item, checkoutAllowedOfferType);

          const shopifyProduct = isShopifyProduct(item.retailerId);

          return (
            <div key={idx} className="price_card">
              <div className="retailer_icon">
                <img alt="retailer" src={item.retailerShortLogo ?? getEntityImage(item.retailerId, 'retailer')}></img>
              </div>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="retailer_price">
                    {shopifyProduct ? (
                      <div className="name" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Buy from us{' '}
                        <RewardPillWithText
                          customPillText={'Exclusive'}
                          rewardAmount={rewardAmount}
                          rewardCampaignType={CampaignType.PurchaseRewardCampaign}
                        />
                      </div>
                    ) : (
                      <a
                        href={item.retailerProductPageUrl}
                        target="_blank"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onOpenRetailerProductPageClick(item);
                        }}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          width: 'fit-content',
                          margin: 0,
                        }}
                        className="external_link bc"
                      >
                        <div className="name">{item.retailerDisplayName || ''}</div>
                      </a>
                    )}
                    <div className="price">{formatMoney(item.retailPrice)}</div>
                  </div>

                  {isOutOfStock && shopifyProduct ? null : (
                    <Button
                      increaseSpecificity
                      variant="solid"
                      sx={(theme) => ({
                        minWidth: 'fit-content',
                        font: theme.vars.font.smButton,
                        fontWeight: theme.typography.smButton.fontWeight,
                        [theme.breakpoints.down('sm')]: {
                          font: theme.vars.font.xsButton,
                          fontWeight: theme.typography.xsButton.fontWeight,
                        },
                      })}
                      height={{ xs: 32, md: 35 }}
                      buttonName={ButtonName.AddToBagModalButton}
                      className={`price_dialog__button--${ButtonName.AddToBagModalButton} bc-dialog-buy-button`}
                      color="primary"
                      {...getBuyOrViewProductMetaTag(
                        isProductEligibleForCheckout(item, checkoutAllowedOfferType) && !disabledCheckout
                          ? 'buy'
                          : 'view',
                        item.retailerDisplayName || '',
                        item.name,
                        item.id,
                        item.stacklineSku,
                        window.location.href,
                        item.retailPrice,
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isProductEligibleForCheckout(item, checkoutAllowedOfferType) && !disabledCheckout) {
                          onBuyClick(item);
                        } else {
                          onOpenRetailerProductPageClick(item);
                        }
                      }}
                    >
                      {getBuyBoxButtonText(item)}
                    </Button>
                  )}
                </div>
                {isOutOfStock ? <OutOfStockPill pillContainerStyle={{}} /> : null}
                {rewardAmount && !isOutOfStock ? (
                  <RewardPillWithText
                    pillContainerStyle={{}}
                    rewardAmount={rewardAmount}
                    rewardPercent={rewardPercent}
                    rewardCampaignType={
                      hasActiveInstantReward
                        ? CampaignType.DailyDealsRewardCampaign
                        : CampaignType.PurchaseRewardCampaign
                    }
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
      <div className="actions"></div>
    </ContentContainer>
  );
};
